import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUpload, 
  faFile, 
  faFilePdf, 
  faImage, 
  faTrash, 
  faEye, 
  faCheck, 
  faTimes,
  faSpinner,
  faExclamationTriangle,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import { documentoAlunoService, TIPOS_DOCUMENTO, STATUS_DOCUMENTO } from '../services/documentoAlunoService';
import ConfirmModal from './ConfirmModal';

/**
 * Componente para upload e gerenciamento de documentos de alunos
 * @param {Object} props
 * @param {string} props.alunoId - ID do aluno
 * @param {number} props.nivelAcesso - Nível de acesso do usuário logado
 * @param {boolean} props.readOnly - Se está em modo somente leitura
 */
const DocumentoUpload = ({ alunoId, nivelAcesso = 0, readOnly = false }) => {
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedTipo, setSelectedTipo] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [documentoToDelete, setDocumentoToDelete] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [documentoToReject, setDocumentoToReject] = useState(null);
  const [motivoRejeicao, setMotivoRejeicao] = useState('');
  const [viewingDocument, setViewingDocument] = useState(null);
  
  const fileInputRef = useRef(null);

  // Carregar documentos quando o alunoId mudar
  useEffect(() => {
    if (alunoId) {
      loadDocumentos();
    }
  }, [alunoId]);

  // Limpar mensagens após alguns segundos
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const loadDocumentos = async () => {
    setLoading(true);
    try {
      const result = await documentoAlunoService.getDocumentos(alunoId);
      if (result.success) {
        setDocumentos(result.data);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Erro ao carregar documentos');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validation = documentoAlunoService.validateFile(file);
      if (!validation.valid) {
        setError(validation.error);
        e.target.value = '';
        return;
      }
      setSelectedFile(file);
      setError('');
      
      // Criar preview se for imagem
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedTipo) {
      setError('Selecione o tipo de documento e o arquivo');
      return;
    }

    setUploading(true);
    setError('');
    
    try {
      const result = await documentoAlunoService.uploadDocumento(alunoId, selectedFile, selectedTipo);
      
      if (result.success) {
        setSuccess(result.message);
        setSelectedFile(null);
        setSelectedTipo('');
        setPreviewUrl(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        await loadDocumentos();
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Erro ao enviar documento');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!documentoToDelete) return;
    
    try {
      const result = await documentoAlunoService.removeDocumento(documentoToDelete.idDocumento);
      if (result.success) {
        setSuccess(result.message);
        await loadDocumentos();
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Erro ao remover documento');
    } finally {
      setShowDeleteConfirm(false);
      setDocumentoToDelete(null);
    }
  };

  const handleAprovar = async (documento) => {
    try {
      const result = await documentoAlunoService.aprovarDocumento(documento.idDocumento);
      if (result.success) {
        setSuccess(result.message);
        await loadDocumentos();
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Erro ao aprovar documento');
    }
  };

  const handleRejeitar = async () => {
    if (!documentoToReject || !motivoRejeicao.trim()) {
      setError('Informe o motivo da rejeição');
      return;
    }
    
    try {
      const result = await documentoAlunoService.rejeitarDocumento(documentoToReject.idDocumento, motivoRejeicao);
      if (result.success) {
        setSuccess(result.message);
        await loadDocumentos();
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Erro ao rejeitar documento');
    } finally {
      setShowRejectModal(false);
      setDocumentoToReject(null);
      setMotivoRejeicao('');
    }
  };

  const handleViewDocument = async (documento) => {
    try {
      const blobUrl = await documentoAlunoService.getDocumentoBlob(documento.nomeArquivo);
      if (blobUrl) {
        setViewingDocument({ ...documento, blobUrl });
      } else {
        setError('Erro ao carregar documento');
      }
    } catch (err) {
      setError('Erro ao visualizar documento');
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.includes('pdf')) {
      return <FontAwesomeIcon icon={faFilePdf} className="text-red-500" />;
    }
    if (mimeType?.startsWith('image/')) {
      return <FontAwesomeIcon icon={faImage} className="text-blue-500" />;
    }
    return <FontAwesomeIcon icon={faFile} className="text-gray-500" />;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case STATUS_DOCUMENTO.AGUARDANDO_REVISAO:
        return <FontAwesomeIcon icon={faClock} className="text-yellow-500" />;
      case STATUS_DOCUMENTO.APROVADO:
        return <FontAwesomeIcon icon={faCheck} className="text-green-500" />;
      case STATUS_DOCUMENTO.REJEITADO:
        return <FontAwesomeIcon icon={faTimes} className="text-red-500" />;
      default:
        return <FontAwesomeIcon icon={faClock} className="text-gray-500" />;
    }
  };

  // Verificar quais tipos de documento já foram enviados
  const getTiposDisponiveis = () => {
    const tiposEnviados = documentos
      .filter(d => d.status !== STATUS_DOCUMENTO.REJEITADO)
      .map(d => d.tipoDocumento);
    return TIPOS_DOCUMENTO.filter(t => !tiposEnviados.includes(t.value));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!alunoId) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Mensagens de feedback */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}
      
      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
          <FontAwesomeIcon icon={faCheck} className="text-green-500" />
          <span className="text-green-700 text-sm">{success}</span>
        </div>
      )}

      {/* Área de Upload - visível apenas se não for readOnly */}
      {!readOnly && (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
            <FontAwesomeIcon icon={faUpload} />
            Enviar Novo Documento
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Seleção do tipo */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-600">
                Tipo de Documento *
              </label>
              <select
                value={selectedTipo}
                onChange={(e) => setSelectedTipo(e.target.value)}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
                disabled={uploading}
              >
                <option value="">Selecione o tipo</option>
                {getTiposDisponiveis().map(tipo => (
                  <option key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Seleção do arquivo */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-600">
                Arquivo (PDF ou Imagem) *
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                onChange={handleFileSelect}
                className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                disabled={uploading}
              />
              <p className="text-xs text-gray-500 mt-1">Máximo: 2MB. Formatos: PDF, JPG, PNG, GIF, WebP</p>
            </div>

            {/* Botão de upload */}
            <div className="flex items-end">
              <button
                type="button"
                onClick={handleUpload}
                disabled={!selectedFile || !selectedTipo || uploading}
                className={`w-full px-4 py-2 rounded font-medium flex items-center justify-center gap-2 ${
                  !selectedFile || !selectedTipo || uploading
                    ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                {uploading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Enviando...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faUpload} />
                    Enviar
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Preview da imagem */}
          {previewUrl && (
            <div className="mt-4">
              <p className="text-sm text-gray-600 mb-2">Preview:</p>
              <img 
                src={previewUrl} 
                alt="Preview" 
                className="max-w-xs max-h-48 rounded border"
              />
            </div>
          )}

          {/* Informação sobre revisão */}
          {nivelAcesso <= 3 && (
            <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-700">
              <FontAwesomeIcon icon={faClock} className="mr-2" />
              Os documentos enviados ficarão aguardando revisão até serem aprovados por um administrador.
            </div>
          )}
        </div>
      )}

      {/* Lista de documentos */}
      <div>
        <h4 className="font-medium text-gray-700 mb-3">
          Documentos Enviados ({documentos.length})
        </h4>
        
        {loading ? (
          <div className="flex items-center justify-center py-8 text-gray-500">
            <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
            Carregando documentos...
          </div>
        ) : documentos.length === 0 ? (
          <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
            <FontAwesomeIcon icon={faFile} className="text-4xl mb-2" />
            <p>Nenhum documento enviado ainda</p>
          </div>
        ) : (
          <div className="space-y-3">
            {documentos.map((documento) => (
              <div 
                key={documento.idDocumento}
                className="p-3 bg-white border rounded-lg hover:shadow-sm transition-shadow"
              >
                {/* Layout mobile-first */}
                <div className="flex flex-col gap-3">
                  {/* Linha 1: Ícone, Título e Status */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {/* Ícone do arquivo */}
                      <div className="text-xl sm:text-2xl flex-shrink-0">
                        {getFileIcon(documento.mimeType)}
                      </div>
                      
                      {/* Nome do documento */}
                      <p className="font-medium text-gray-800 text-sm sm:text-base truncate">
                        {documentoAlunoService.getTipoLabel(documento.tipoDocumento)}
                      </p>
                    </div>
                    
                    {/* Status badge */}
                    <div className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 flex-shrink-0 whitespace-nowrap ${documentoAlunoService.getStatusColor(documento.status)}`}>
                      {getStatusIcon(documento.status)}
                      <span className="hidden xs:inline">{documentoAlunoService.getStatusLabel(documento.status)}</span>
                    </div>
                  </div>
                  
                  {/* Linha 2: Data e motivo rejeição */}
                  <div className="text-xs text-gray-500 pl-7 sm:pl-9">
                    <p>Enviado em: {formatDate(documento.dataUpload)}</p>
                    {documento.motivoRejeicao && (
                      <p className="text-red-600 mt-1">
                        Motivo: {documento.motivoRejeicao}
                      </p>
                    )}
                  </div>
                  
                  {/* Linha 3: Botões de ação */}
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                    {/* Visualizar */}
                    <button
                      type="button"
                      onClick={() => handleViewDocument(documento)}
                      className="flex-1 sm:flex-none px-3 py-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded flex items-center justify-center gap-1 text-sm"
                      title="Visualizar"
                    >
                      <FontAwesomeIcon icon={faEye} />
                      <span>Ver</span>
                    </button>
                    
                    {/* Ações de aprovação (apenas para nível > 3) */}
                    {nivelAcesso > 3 && documento.status === STATUS_DOCUMENTO.AGUARDANDO_REVISAO && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleAprovar(documento)}
                          className="flex-1 sm:flex-none px-3 py-2 text-white bg-green-600 hover:bg-green-700 rounded flex items-center justify-center gap-1 text-sm"
                          title="Aprovar"
                        >
                          <FontAwesomeIcon icon={faCheck} />
                          <span className="hidden sm:inline">Aprovar</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setDocumentoToReject(documento);
                            setShowRejectModal(true);
                          }}
                          className="flex-1 sm:flex-none px-3 py-2 text-white bg-red-600 hover:bg-red-700 rounded flex items-center justify-center gap-1 text-sm"
                          title="Rejeitar"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                          <span className="hidden sm:inline">Rejeitar</span>
                        </button>
                      </>
                    )}
                    
                    {/* Excluir (apenas se não for readOnly e se for o próprio documento ou nível > 3) */}
                    {!readOnly && (nivelAcesso > 3 || documento.status !== STATUS_DOCUMENTO.APROVADO) && (
                      <button
                        type="button"
                        onClick={() => {
                          setDocumentoToDelete(documento);
                          setShowDeleteConfirm(true);
                        }}
                        className="flex-1 sm:flex-none px-3 py-2 text-red-600 bg-red-50 hover:bg-red-100 rounded flex items-center justify-center gap-1 text-sm"
                        title="Excluir"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                        <span className="hidden sm:inline">Excluir</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de visualização de documento */}
      {viewingDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-3 sm:p-4 border-b flex items-center justify-between gap-2">
              <h3 className="font-medium text-sm sm:text-base truncate">
                {documentoAlunoService.getTipoLabel(viewingDocument.tipoDocumento)}
              </h3>
              <button
                onClick={() => {
                  if (viewingDocument.blobUrl) {
                    URL.revokeObjectURL(viewingDocument.blobUrl);
                  }
                  setViewingDocument(null);
                }}
                className="p-2 hover:bg-gray-100 rounded flex-shrink-0"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-2 sm:p-4 bg-gray-100">
              {viewingDocument.mimeType?.includes('pdf') ? (
                <iframe
                  src={viewingDocument.blobUrl}
                  className="w-full h-[60vh] sm:h-[70vh]"
                  title="Visualização do documento"
                />
              ) : (
                <img
                  src={viewingDocument.blobUrl}
                  alt={viewingDocument.tipoDocumento}
                  className="max-w-full h-auto mx-auto"
                />
              )}
            </div>
            {/* Rodapé com ações - visível para usuários com nível > 3 e documento aguardando revisão */}
            {nivelAcesso > 3 && viewingDocument.status === STATUS_DOCUMENTO.AGUARDANDO_REVISAO && (
              <div className="p-3 sm:p-4 border-t bg-gray-50 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (viewingDocument.blobUrl) {
                      URL.revokeObjectURL(viewingDocument.blobUrl);
                    }
                    setViewingDocument(null);
                  }}
                  className="w-full sm:w-auto px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                >
                  Fechar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDocumentoToReject(viewingDocument);
                    setShowRejectModal(true);
                    if (viewingDocument.blobUrl) {
                      URL.revokeObjectURL(viewingDocument.blobUrl);
                    }
                    setViewingDocument(null);
                  }}
                  className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center justify-center gap-2"
                >
                  <FontAwesomeIcon icon={faTimes} />
                  Rejeitar
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await handleAprovar(viewingDocument);
                    if (viewingDocument.blobUrl) {
                      URL.revokeObjectURL(viewingDocument.blobUrl);
                    }
                    setViewingDocument(null);
                  }}
                  className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center justify-center gap-2"
                >
                  <FontAwesomeIcon icon={faCheck} />
                  Aprovar
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de confirmação de exclusão */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onConfirm={handleDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setDocumentoToDelete(null);
        }}
        title="Confirmar Exclusão"
        message={`Deseja realmente excluir o documento "${documentoToDelete ? documentoAlunoService.getTipoLabel(documentoToDelete.tipoDocumento) : ''}"?`}
        confirmType="delete"
      />

      {/* Modal de rejeição */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-4 sm:p-6">
            <h3 className="font-bold text-base sm:text-lg mb-4">Rejeitar Documento</h3>
            <p className="text-gray-600 text-sm mb-4">
              Informe o motivo da rejeição do documento "{documentoToReject ? documentoAlunoService.getTipoLabel(documentoToReject.tipoDocumento) : ''}":
            </p>
            <textarea
              value={motivoRejeicao}
              onChange={(e) => setMotivoRejeicao(e.target.value)}
              className="w-full border rounded px-3 py-2 h-24 focus:outline-none focus:ring-2 focus:ring-red-400 text-sm"
              placeholder="Digite o motivo da rejeição..."
            />
            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 mt-4">
              <button
                type="button"
                onClick={() => {
                  setShowRejectModal(false);
                  setDocumentoToReject(null);
                  setMotivoRejeicao('');
                }}
                className="w-full sm:w-auto px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 order-2 sm:order-1"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleRejeitar}
                disabled={!motivoRejeicao.trim()}
                className={`w-full sm:w-auto px-4 py-2 rounded text-white order-1 sm:order-2 ${
                  motivoRejeicao.trim() 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-red-300 cursor-not-allowed'
                }`}
              >
                Rejeitar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentoUpload;
